// src/InfoUtil.js
import React, { useEffect, useState } from "react";
import { firestore } from "./firebase";
import "./InfoUtil.css";

function InformacionUtil() {
  // Precios dinámicos (Firestore)
  const [precios, setPrecios] = useState({
    precioAlta: 3000,   // enero / febrero
    precioMedia: 2800,  // diciembre / marzo
    precioBaja: 2500,   // resto del año
  });

  // Tab actual
  const TABS = ["reservas", "camping", "pagos", "mapa", "inventario"];
  const [tab, setTab] = useState("reservas");

  // Lightbox para el mapa
  const [mapaAbierto, setMapaAbierto] = useState(false);

  // Inventario “limpio” (extraído de tu imagen)
  const inventarioCasa = [
    "8 tenedores","8 cucharas","8 cucharitas","8 cuchillos",
    "1 espumadera","1 cucharón","1 tabla de picar",
    "8 vasos","8 tazas","8 platos","8 compoteras",
    "1 jarra de leche","1 jarra de jugo",
    "1 olla grande","1 jarro de aluminio","1 colador",
    "1 bowl acero inoxidable","1 bowl aluminio chico",
    "1 disco para cocinar","1 rallador","1 encendedor",
    "4 sillas de madera","4 sillas de plástico",
    "8 colchones","8 almohadas",
    "1 mantel","1 escoba","1 pala de plástico","1 lampazo",
    "1 paño de piso","1 rejilla","1 repasador",
    "1 garrafa 3 kg","1 parrilla","1 pala para brasa","1 gancho para brasa",
    "1 sartén"
  ];

  // Precios camping / día (config/precios_extra, los mismos del panel)
  const [preciosExtra, setPreciosExtra] = useState({ camping: 290, dia: 145 });
  useEffect(() => {
    const unsub = firestore.collection("config").doc("precios_extra").onSnapshot((doc) => {
      const d = doc.data() || {};
      setPreciosExtra({
        camping: Number(d.campingPorPersona ?? 290),
        dia: Number(d.diaPorPersona ?? 145),
      });
    });
    return () => unsub && unsub();
  }, []);

  // Firestore: precios
  useEffect(() => {
    const unsub = firestore.collection("config").doc("precios").onSnapshot((doc) => {
      const d = doc.data() || {};
      setPrecios({
        precioAlta:  Number(d.precioAlta  ?? 3000),
        precioMedia: Number(d.precioMedia ?? 2800),
        precioBaja:  Number(d.precioBaja  ?? 2500),
      });
    });
    return () => unsub && unsub();
  }, []);

  // Deep-link a pestañas con hash (#mapa, #inventario, #pagos, #reservas)
  // y asegurar que la vista arranca arriba
  useEffect(() => {
    const h = (window.location.hash || "").replace("#", "");
    if (TABS.includes(h)) setTab(h);

    // Evita que el navegador restaure una posición previa
    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch {}

    // Subir al tope al entrar
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (TABS.includes(tab)) {
      // no empuja historial
      window.history.replaceState(null, "", `#${tab}`);

      // Siempre mantener la vista arriba al cambiar de tab
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [tab]);

  const fmt = (n) => `UYU ${Number(n).toLocaleString("es-UY")}`;

  // ====== Contenido de pestañas ======
  const TabReservas = () => (
    <div className="tab-panel" role="tabpanel" id="panel-reservas" aria-labelledby="tab-reservas">
      <h1 className="info-title">🏡 Información útil</h1>

      <section className="info-section">
        <h2>📌 General</h2>
        <p>
          Este reglamento aplica a todas las personas que hacen uso de las casas en el Parque de Retiros de
          Cuchilla Alta. Para garantizar disponibilidad y orden, es obligatorio realizar una reserva previa.
        </p>
      </section>

      <section className="info-section">
        <h2>🗓️ Cómo reservar</h2>
        <p>
          Realizá la reserva en la web. Creá un usuario y ya podés reservar.
          <br />
          👉{" "}
          <a href="https://reservascuchi.com" target="_blank" rel="noopener noreferrer">
            www.reservascuchi.com
          </a>
        </p>

        <h3>📅 Detalles</h3>
        <ul>
          <li>La reserva incluye número de casa, fechas y horarios.</li>
          <li>Check-in: <b>12:00</b></li>
          <li>Check-out: <b>9:30</b></li>
          <li>Máximo: <strong>2 casas por familia</strong> para las mismas fechas.</li>
        </ul>
        <p className="info-note">Los horarios pueden ajustarse según limpieza y ocupación.</p>
        <p className="info-note">
          La casa asignada podría variar respecto a la solicitada para optimizar ocupación (se avisará con
          anticipación).
        </p>

        <p className="info-alert">
          ❗ La Casa N°1 está reservada para Mini, Maxi y Campamento de Matrimonios (aparece bloqueada en el
          calendario durante esos eventos). También pueden aparecer otras casas bloqueadas en determinados momentos del año por eventos y otras actividades.
        </p>
      </section>

      <section className="info-section">
        <h2>⏳ Anticipación</h2>
        <ul>
          <li>
            <b>Miembros:</b> el <b>1 de cada mes</b> se habilita reservar cualquier estadía cuyo
            <b> check-in caiga</b> en el mes que está a <b>3 meses</b> de distancia.
            El <i>check-out</i> puede ser en el mes siguiente.
          </li>
          <li>
            <b>No miembros:</b> mismo criterio, pero con <b>1 mes</b> de anticipación.
          </li>
        </ul>
        <p className="info-note">
          Ejemplo: el <b>1/9</b> se habilita todo <b>diciembre</b> (reservas que <u>empiezan</u> en diciembre),
          incluso del <b>31/12 al 10/1</b>. El <b>1/10</b> se habilita todo <b>enero</b>; el <b>1/11</b>, todo <b>febrero</b>.
        </p>
      </section>

      <section className="info-section">
        <h2>🛏️ Duración</h2>
        <ul>
          <li>Enero y febrero: <b>mín. 5</b> y <b>máx. 10</b> noches.</li>
          <li>Resto del año: hasta <b>14</b> noches.</li>
        </ul>
        <p className="info-note">
          * Si faltan <b>menos de 30 días</b> para el inicio de la estadía, el mínimo de 5 noches en
          enero y febrero <b>no aplica</b>.
        </p>
      </section>

      <section className="info-section">
        <h2>💵 Tarifas por noche</h2>
        <ul>
          <li><b>Alta</b> (enero/febrero): {fmt(precios.precioAlta)}</li>
          <li><b>Media</b> (diciembre/marzo): {fmt(precios.precioMedia)}</li>
          <li><b>Baja</b> (resto del año): {fmt(precios.precioBaja)}</li>
        </ul>
      </section>
    </div>
  );

  const TabCampingDia = () => (
    <div className="tab-panel" role="tabpanel" id="panel-camping" aria-labelledby="tab-camping">
      <h1 className="info-title">🏕️ Camping y estadía por el día</h1>

      <section className="info-section">
        <h2>⛺ Camping</h2>
        <ul>
          <li>Registrá tu estadía en la web, en la sección <b>Camping</b>, indicando personas y noches.</li>
          <li>Tarifa: <b>{fmt(preciosExtra.camping)}</b> por persona por noche.</li>
        </ul>
      </section>

      <section className="info-section">
        <h2>🌞 Por el día</h2>
        <ul>
          <li>Registrá tu visita en la web, en la sección <b>Día</b>, indicando la cantidad de personas.</li>
          <li>Tarifa: <b>{fmt(preciosExtra.dia)}</b> por persona por día.</li>
        </ul>
      </section>

      <section className="info-section">
        <h2>📌 Para ambos</h2>
        <ul>
          <li>👶 Los niños menores de 12 años <b>no pagan</b> ni <b>se registran</b>.</li>
          <li>Se puede registrar el <b>mismo día</b>; no hace falta anticipación, aunque siempre es recomendable.</li>
          <li>Servicios: baños, duchas con agua caliente, luz, parrilla y más.</li>
        </ul>
        <p className="info-note">
          Para pagar, seguí los pasos de la pestaña <b>Cómo pagar</b>.
        </p>
      </section>
    </div>
  );

  const TabPagos = () => (
    <div className="tab-panel" role="tabpanel" id="panel-pagos" aria-labelledby="tab-pagos">
      <h2>💰 Pagos</h2>
      <p>El total aparece antes de confirmar y también en el correo de confirmación.</p>
      <p>Se debe pagar la totalidad del monto antes de ocupar.</p>

      <h3>💳 Depósitos en Banco ITAU</h3>
      <ul>
        <li>N° de cuenta: <strong>95 68 450</strong> (Caja de ahorros, $ uruguayos)</li>
        <li>A nombre de: <strong>Congregación Menonita de Montevideo</strong></li>
        <li>(También se puede depositar en cuentas de las Colonias)</li>
      </ul>

      <p>
        ✅ Enviá el comprobante de pago a Ricardo Bachmann (con nombre y fecha de ingreso) por WhatsApp/SMS:
        <br />
        📱{" "}
        <strong>
          <a href="https://wa.me/59899503445" target="_blank" rel="noopener noreferrer">
            099 503 445
          </a>
        </strong>
      </p>
      <p className="info-note">💸 ABITAB acepta depósitos a ITAU, sin costo adicional.</p>

      <section className="info-section">
        <p className="info-note">
          Tras recibir el comprobante, el administrador <b>confirma el pago</b>. Podrás ver el estado
          <b> “Pago confirmado”</b> en <b>Mis Reservas</b> (casas) o en tus registros de <b>Camping</b> / <b>Día</b>.
        </p>
      </section>
    </div>
  );

  const TabMapa = () => (
    <div className="tab-panel" role="tabpanel" id="panel-mapa" aria-labelledby="tab-mapa">
      <h2>🗺️ Mapa del predio</h2>
      <p>Ubicación de las cuatro casas y acceso vehicular.</p>

      <img
        src="/img/mapa-predio.png"
        alt="Mapa del Parque de Retiros: ubicación de Casas 1–4 y acceso por Río Negro"
        className="mapa-thumb"
        loading="lazy"
        onClick={() => setMapaAbierto(true)}
      />
      <button className="btn-link" onClick={() => setMapaAbierto(true)}>Ver mapa ampliado</button>

      {mapaAbierto && (
        <div className="lightbox" role="dialog" aria-modal="true">
          <button className="lightbox-close" onClick={() => setMapaAbierto(false)} aria-label="Cerrar">×</button>
          <img src="/img/mapa-predio.png" alt="" />
        </div>
      )}
    </div>
  );

  const TabInventario = () => (
    <div className="tab-panel" role="tabpanel" id="panel-inventario" aria-labelledby="tab-inventario">
      <h2>🧰 Inventario de las casas</h2>
      <p>Incluye vajilla y utensilios básicos, parrilla y garrafa de 3 kg.</p>

      <details open>
        <summary>Ver lista completa ({inventarioCasa.length} ítems)</summary>
        <ul className="inventario-grid">
          {inventarioCasa.map((i) => <li key={i}>{i}</li>)}
        </ul>
      </details>

      <p className="tip">Recomendamos traer: sábanas, toallas, artículos de higiene y repasadores extra.</p>
    </div>
  );

  return (
    <div className="info-container">
      {/* ---- Tabs ---- */}
      <div className="tabs" role="tablist" aria-label="Información útil">
        <button
          id="tab-reservas"
          role="tab"
          aria-selected={tab === "reservas"}
          aria-controls="panel-reservas"
          className={`tab-btn ${tab === "reservas" ? "active" : ""}`}
          onClick={() => setTab("reservas")}
        >
          Casas
        </button>

        <button
          id="tab-camping"
          role="tab"
          aria-selected={tab === "camping"}
          aria-controls="panel-camping"
          className={`tab-btn ${tab === "camping" ? "active" : ""}`}
          onClick={() => setTab("camping")}
        >
          Camping y Día
        </button>

        <button
          id="tab-pagos"
          role="tab"
          aria-selected={tab === "pagos"}
          aria-controls="panel-pagos"
          className={`tab-btn ${tab === "pagos" ? "active" : ""}`}
          onClick={() => setTab("pagos")}
        >
          Cómo pagar
        </button>

        <button
          id="tab-mapa"
          role="tab"
          aria-selected={tab === "mapa"}
          aria-controls="panel-mapa"
          className={`tab-btn ${tab === "mapa" ? "active" : ""}`}
          onClick={() => setTab("mapa")}
        >
          Mapa
        </button>

        <button
          id="tab-inventario"
          role="tab"
          aria-selected={tab === "inventario"}
          aria-controls="panel-inventario"
          className={`tab-btn ${tab === "inventario" ? "active" : ""}`}
          onClick={() => setTab("inventario")}
        >
          Inventario casas
        </button>
      </div>

      {/* ---- Paneles ---- */}
      {tab === "reservas" && <TabReservas />}
      {tab === "camping" && <TabCampingDia />}
      {tab === "pagos" && <TabPagos />}
      {tab === "mapa" && <TabMapa />}
      {tab === "inventario" && <TabInventario />}
    </div>
  );
}

export default InformacionUtil;